import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, of, map, catchError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../components/auth.service';
import { Booking, BookingStatus } from '../../models/booking.model';
import {
  ProfileUpdateRequest,
  ProfileUpdateResponse,
  UserProfileResponse,
  UserMembership,
  CourseSessionSummary,
  CourseSessionInfo,
  ProfileDashboardData,
  UpcomingActivity,
  ActivityType,
  RemovedMembership,
  MyWorkHoursSummary,
  MyFeeYear,
} from '../models/profile.interface';
import { BookingType } from '../../models/booking.model';
import { MySubscriptionSummary } from '../../features/subscriptions/models/subscription.interface';

/** Number of upcoming activities to display on the profile dashboard */
const DASHBOARD_ACTIVITY_LIMIT = 5;

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly apiUrl = environment.apiUrl;

  /**
   * Get the full profile of the currently authenticated user.
   * Use this instead of the JWT-hydrated session when you need the rich
   * profile fields (address, contact, general info) that aren't in the JWT.
   */
  getProfile(): Observable<UserProfileResponse> {
    return this.http.get<UserProfileResponse>(`${this.apiUrl}/users/profile`);
  }

  /**
   * Update user profile. Any subset of fields may be sent; only provided
   * fields are touched on the backend. Empty strings clear nullable fields.
   */
  updateProfile(data: ProfileUpdateRequest): Observable<ProfileUpdateResponse> {
    return this.http.patch<ProfileUpdateResponse>(
      `${this.apiUrl}/users/profile`,
      data
    );
  }

  /**
   * Get user's bookings and filter for upcoming ones.
   *
   * Uses server-side filtering via the startTimeAfter parameter for precise
   * datetime filtering. Only returns bookings starting after the current time.
   *
   * See docs/TIMEZONE_HANDLING.md for the timezone architecture.
   */
  getUpcomingBookings(limit: number = 5): Observable<Booking[]> {
    const currentUser = this.authService.currentUserSignal();
    if (!currentUser) {
      return of([]);
    }

    // Use precise datetime filter - backend handles all filtering
    const params = new HttpParams()
      .set('startTimeAfter', new Date().toISOString())
      .set('status', BookingStatus.CONFIRMED)
      .set('sortBy', 'startTime')
      .set('sortDirection', 'asc')
      .set('limit', limit.toString());

    return this.http
      .get<{ items: Booking[] }>(
        `${this.apiUrl}/bookings/me`,
        { params }
      )
      .pipe(
        map((response) => response.items),
        catchError(() => of([]))
      );
  }

  /**
   * Get all user's memberships across all organizations
   */
  getMyMemberships(): Observable<UserMembership[]> {
    return this.http
      .get<UserMembership[]>(`${this.apiUrl}/users/me/memberships`)
      .pipe(catchError(() => of([])));
  }

  /**
   * Get removed memberships for a specific organization
   */
  getMyRemovedMemberships(organizationId: string): Observable<RemovedMembership[]> {
    return this.http
      .get<RemovedMembership[]>(`${this.apiUrl}/organizations/${organizationId}/my-removed-memberships`)
      .pipe(catchError(() => of([])));
  }

  getMyWorkHours(organizationId: string, year?: number): Observable<MyWorkHoursSummary | null> {
    let params = new HttpParams();
    if (year != null) {
      params = params.set('year', String(year));
    }
    return this.http.get<MyWorkHoursSummary>(
      `${this.apiUrl}/organizations/${organizationId}/my-work-hours`,
      { params }
    ).pipe(catchError(() => of(null)));
  }

  /**
   * What the member owes per membership type and year.
   *
   * Deliberately not the row endpoint (`my-fees`): a journal row's `amount` is a
   * signed contribution, so a base of 100 € with a −20 € reduction renders as
   * two charges there and never as the 80 € actually owed.
   */
  getMyFeeYears(organizationId: string): Observable<MyFeeYear[]> {
    return this.http
      .get<MyFeeYear[]>(`${this.apiUrl}/organizations/${organizationId}/my-fee-years`)
      .pipe(catchError(() => of([])));
  }

  /**
   * Get user's course sessions (upcoming, past, waitlist)
   */
  getMyCoursesSessions(): Observable<CourseSessionSummary> {
    return this.http
      .get<CourseSessionSummary>(`${this.apiUrl}/me/course-sessions`)
      .pipe(
        catchError(() =>
          of({
            upcoming: [],
            past: [],
            waitlist: [],
          })
        )
      );
  }

  /**
   * Get user's active subscriptions
   */
  getMySubscriptions(): Observable<MySubscriptionSummary[]> {
    return this.http
      .get<MySubscriptionSummary[]>(`${this.apiUrl}/subscriptions/my-subscriptions`)
      .pipe(catchError(() => of([])));
  }

  /**
   * Load all dashboard data in parallel
   */
  loadDashboardData(): Observable<ProfileDashboardData> {
    return forkJoin({
      upcomingBookings: this.getUpcomingBookings(DASHBOARD_ACTIVITY_LIMIT),
      memberships: this.getMyMemberships(),
      courseSessions: this.getMyCoursesSessions(),
      subscriptions: this.getMySubscriptions(),
    }).pipe(
      map((data) => {
        // Create unified activities from bookings and course sessions
        const upcomingActivities = this.mergeActivities(
          data.upcomingBookings,
          data.courseSessions.upcoming
        );

        return {
          upcomingBookings: data.upcomingBookings,
          upcomingActivities,
          memberships: data.memberships,
          courseSessions: data.courseSessions,
          subscriptionsCount: data.subscriptions.filter(
            (s) => s.status === 'active'
          ).length,
        };
      })
    );
  }

  /**
   * Merge bookings and course sessions into unified activities sorted by start time
   */
  private mergeActivities(
    bookings: Booking[],
    courseSessions: CourseSessionInfo[]
  ): UpcomingActivity[] {
    const activities: UpcomingActivity[] = [];

    // Convert bookings to activities
    for (const booking of bookings) {
      const activityType = this.getActivityTypeFromBooking(booking);
      activities.push({
        id: booking.id,
        type: activityType,
        startTime: booking.start_time,
        endTime: booking.end_time,
        title: activityType === 'course' && booking.course_name
          ? booking.course_name
          : booking.court_name,
        subtitle: this.getBookingSubtitle(booking, activityType),
        status: booking.status,
        booking,
      });
    }

    // Convert course sessions to activities (only if not already in bookings)
    const bookingSessionIds = new Set(
      bookings
        .filter((b) => b.course_session_id)
        .map((b) => b.course_session_id)
    );

    for (const session of courseSessions) {
      // Skip if this session is already represented as a booking
      if (bookingSessionIds.has(session.id)) {
        continue;
      }

      activities.push({
        id: session.id,
        type: 'course',
        startTime: session.startTime,
        endTime: session.endTime,
        title: session.courseName || 'Kurs',
        subtitle: session.instructorName,
        status: session.status,
        courseSession: session,
      });
    }

    // Sort by start time and limit to dashboard display count
    return activities
      .sort(
        (a, b) =>
          new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      )
      .slice(0, DASHBOARD_ACTIVITY_LIMIT);
  }

  /**
   * Get activity type from booking's booking_type
   */
  private getActivityTypeFromBooking(booking: Booking): ActivityType {
    if (booking.booking_type === BookingType.COURSE) {
      return 'course';
    }
    if (booking.booking_type === BookingType.SUBSCRIPTION) {
      return 'subscription';
    }
    return 'individual';
  }

  /**
   * Get subtitle text based on booking type
   */
  private getBookingSubtitle(booking: Booking, activityType: ActivityType): string | undefined {
    if (activityType === 'course') {
      return booking.session_name || booking.court_name;
    }
    if (activityType === 'subscription') {
      return booking.category_name ? `Abo · ${booking.category_name}` : 'Abonnement';
    }
    return booking.category_name;
  }
}
